import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiArrowRight,
  FiClock,
  FiAlertCircle,
} from 'react-icons/fi';
import { formatMoney } from '../utils/money';
import PlanUpgradeCard from './PlanUpgradeCard';
import CollectionTrendChart from './CollectionTrendChart';
import { canAccessCobranzasEssential } from '../utils/profesionalAccess';

function MetricTile({ label, value, hint, hintTone, modoOscuro, highlight }) {
  const tile = highlight
    ? modoOscuro ? 'bg-[#1F80FF]/10 ring-1 ring-[#1F80FF]/30' : 'bg-[#1F80FF]/5 ring-1 ring-[#1F80FF]/20'
    : modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50';

  return (
    <div className={`rounded-xl px-3 py-2.5 ${tile}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-lg sm:text-xl font-semibold tabular-nums mt-0.5">{value}</div>
      {hint && (
        <div className={`text-[10px] mt-0.5 font-medium ${hintTone || 'text-gray-400'}`}>{hint}</div>
      )}
    </div>
  );
}

function WidgetSkeleton({ box, modoOscuro }) {
  const pulse = modoOscuro ? 'bg-gray-800 animate-pulse' : 'bg-gray-200 animate-pulse';
  return (
    <div className={`rounded-2xl border overflow-hidden ${box}`}>
      <div className="p-4 space-y-3">
        <div className={`h-4 w-40 rounded ${pulse}`} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`h-16 rounded-xl ${pulse}`} />
          ))}
        </div>
        <div className={`h-12 rounded-xl ${pulse}`} />
      </div>
    </div>
  );
}

export default function CobranzasInicioWidget({
  secureFetch,
  hasProfesional,
  user,
  modoOscuro,
  box,
  textPri,
  textSec,
  textMuted,
}) {
  const hasCobranzasEssential = user ? canAccessCobranzasEssential(user) : hasProfesional;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tendencias, setTendencias] = useState(null);

  const green = modoOscuro ? 'text-green-300' : 'text-green-700';
  const red = modoOscuro ? 'text-red-300' : 'text-red-700';
  const amber = modoOscuro ? 'text-amber-300' : 'text-amber-700';

  const fetchResumen = useCallback(async () => {
    setLoading(true);
    try {
      const [resResumen, resTrend] = await Promise.all([
        secureFetch('/api/reportes/cobranzas-resumen'),
        hasProfesional ? secureFetch('/api/reportes/cobranzas-tendencias?meses=6') : Promise.resolve(null),
      ]);
      if (resResumen.ok) setData(await resResumen.json());
      else setData(null);
      if (resTrend?.ok) setTendencias(await resTrend.json());
      else setTendencias(null);
    } catch {
      setData(null);
      setTendencias(null);
    } finally {
      setLoading(false);
    }
  }, [secureFetch, hasProfesional]);

  useEffect(() => {
    fetchResumen();
  }, [fetchResumen]);

  if (!hasCobranzasEssential) {
    return (
      <PlanUpgradeCard
        compact
        description="Seguí la tasa de cobranza y pendientes en Inicio con plan Básico o superior."
        planLabel="Básico"
        modoOscuro={modoOscuro}
      />
    );
  }

  if (loading) return <WidgetSkeleton box={box} modoOscuro={modoOscuro} />;

  if (!data?.mesActual) return null;

  const cur = data.mesActual;
  const varTasa = data.variacion?.tasaCobranza ?? 0;
  const varPend = data.variacion?.pendienteActual ?? 0;
  const varDso = data.variacion?.dso ?? 0;
  const tasaTone = cur.tasaCobranza >= 80 ? green : amber;

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`}>
      <button
        type="button"
        onClick={() => navigate('/reportes?tab=cobranzas')}
        className={`w-full text-left transition-colors ${modoOscuro ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50/80'}`}
      >
        <div className={`px-4 py-3 border-b flex items-start justify-between gap-3 ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'
              }`}>
                <FiDollarSign className="w-3 h-3" />
                Análisis de cobranza
              </span>
              {cur.vencidas > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  modoOscuro ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-700'
                }`}>
                  <FiAlertCircle className="w-3 h-3" />
                  {cur.vencidas} vencida(s)
                </span>
              )}
            </div>
            <h2 className={`text-sm font-semibold ${textPri}`}>Resumen de cobranza</h2>
            <p className={`text-xs mt-0.5 ${textSec}`}>{cur.label}</p>
          </div>
          <span className={`shrink-0 text-xs font-semibold text-[#1F80FF] inline-flex items-center gap-1 mt-1`}>
            Ver análisis
            <FiArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <MetricTile
              label="Tasa de cobro"
              value={<span className={tasaTone}>{cur.tasaCobranza}%</span>}
              hint={
                <span className="inline-flex items-center gap-1">
                  {varTasa > 0 ? <FiTrendingUp className="w-3 h-3" /> : varTasa < 0 ? <FiTrendingDown className="w-3 h-3" /> : null}
                  {varTasa > 0 ? '+' : ''}{varTasa} pp vs anterior
                </span>
              }
              hintTone={varTasa >= 0 ? green : red}
              modoOscuro={modoOscuro}
              highlight
            />
            <MetricTile
              label="Pendiente"
              value={<span className={textPri}>{formatMoney(cur.pendienteActual, 'ARS')}</span>}
              hint={`${varPend > 0 ? '+' : ''}${formatMoney(varPend, 'ARS')} vs anterior`}
              hintTone={varPend > 0 ? red : green}
              modoOscuro={modoOscuro}
            />
            {hasProfesional ? (
              <MetricTile
                label="DSO cartera"
                value={
                  <span className={`inline-flex items-center gap-1 ${textPri}`}>
                    <FiClock className="w-4 h-4 opacity-50" />
                    {cur.dso}d
                  </span>
                }
                hint={`${varDso > 0 ? '+' : ''}${varDso}d vs anterior`}
                hintTone={varDso > 0 ? amber : green}
                modoOscuro={modoOscuro}
              />
            ) : (
              <MetricTile
                label="Vencidas"
                value={<span className={cur.vencidas > 0 ? amber : textPri}>{cur.vencidas}</span>}
                hint="Fuera de plazo"
                modoOscuro={modoOscuro}
              />
            )}
            <MetricTile
              label="Impagas"
              value={<span className={cur.impagas > 0 ? amber : textPri}>{cur.impagas}</span>}
              hint={`${formatMoney(cur.facturado, 'ARS')} facturado`}
              modoOscuro={modoOscuro}
            />
          </div>

          {tendencias?.points?.length > 1 && (
            <CollectionTrendChart
              points={tendencias.points}
              modoOscuro={modoOscuro}
              textPri={textPri}
              textMuted={textMuted}
              compact
            />
          )}
        </div>
      </button>
    </section>
  );
}
